import { ModelType, PublicationSheetWithId } from '@livv/models';
import { getSheetLink } from '@livv/utils/helpers';
import { SxProps, Typography, Box } from '@mui/material';
import { convertFromRaw } from 'draft-js';
import Script from 'next/script';
import useTranslation from 'next-translate/useTranslation';
import { FC } from 'react';
import Note from '@components/Editor/Note';
import { DisplayParagraph } from '@components/Editor/Viewer';
import { ButtonLink } from '@components/Links';
import Paywall from '@components/Paywall';
import PrintButton from '@components/PrintButton';
import ScrollToTopButton from '@components/ScrollToTopButton';
import { estimateReadTime } from '@utils/consts/readTime';

const textSx: SxProps = {
    '& sup': {
        cursor: 'pointer',
        pl: 3,
        pr: 3,
    },
    mt: 5,
};

interface PublicationSheetContentProps {
    customPrintHandler?: () => void;
    lightContent?: boolean;
    sheet: PublicationSheetWithId;
    showOpenInBookButton?: boolean;
    showPrintButton?: boolean;
}

const PublicationSheetContent: FC<PublicationSheetContentProps> = ({
    customPrintHandler,
    lightContent = false,
    sheet: { content, id: sheetId, number, keyword, title, titleNote },
    showOpenInBookButton = false,
    showPrintButton = false,
}) => {
    const { t } = useTranslation('books');
    const readTime = estimateReadTime(convertFromRaw(content).getPlainText());
    const fullTitle: string = lightContent ? title : `${number}. ${title}`;

    // https://developers.google.com/search/docs/appearance/structured-data/paywalled-content
    const jsonData = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        hasPart: {
            '@type': 'WebPageElement',
            cssSelector: '.paywall-container',
            isAccessibleForFree: 'False',
        },
        headline: title,
        isAccessibleForFree: 'False',
    };

    return (
        <>
            <Script
                dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonData) }}
                strategy="beforeInteractive" // with beforeInteractive, script is injected in head
                type="application/ld+json"
            />
            <Box
                sx={{
                    alignItems: 'start',
                    display: 'flex',
                    flexDirection: { sm: 'row', xs: 'column' },
                    gap: 3,
                    justifyContent: 'space-between',
                }}
            >
                <Box sx={{ flex: 1 }}>
                    <Typography
                        data-test-id={`PublicationSheet-${sheetId}`}
                        variant="h2"
                        gutterBottom
                    >
                        {fullTitle}
                        {titleNote && (
                            <Note
                                content={titleNote}
                                noteIndex={1}
                                sx={{
                                    color: 'common.white',
                                    fontFamily: 'fontFamily',
                                    fontSize: 'body1.fontSize',
                                    lineHeight: 'body1.lineHeight',
                                }}
                            />
                        )}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        {`${readTime}${t('readTime', { count: readTime })}`}
                    </Typography>
                </Box>
                {showPrintButton && (
                    <PrintButton
                        customButtonHandler={customPrintHandler}
                        documentId={sheetId}
                        documentName={title}
                        documentType={ModelType.PUBLICATION_SHEET}
                        plainButton
                    />
                )}
            </Box>
            <Typography component="div" sx={textSx}>
                <DisplayParagraph shiftNotes={Boolean(titleNote)} value={content} />
                {/* Need .paywall-container for seo json */}
                <div className="paywall-container">
                    <Paywall />
                </div>
                <ScrollToTopButton />
            </Typography>
            {showOpenInBookButton && (
                <ButtonLink
                    color="secondary"
                    href={getSheetLink({
                        bookId: keyword.treeId,
                        bookPath: keyword.path.join('/'),
                        sheetId,
                    })}
                    sx={{ mt: 2 }}
                    variant="contained"
                    openInNewTab
                >
                    {t('openBook')}
                </ButtonLink>
            )}
        </>
    );
};

export default PublicationSheetContent;
